import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography"
import { styled } from "@mui/material/styles";
import Guid from "_shared/guids/guid";
import { useMemo } from "react";
import UserCoworker from "users/information/coworker-information/user-coworker";
import UserInformation from "users/information/user-information";
import _ from "lodash";

const MainContainer = styled("div")(() => ({
  display: "flex",
  flexDirection: "column",
  height: "100%",
}));
const MainContent = styled("div")(() => ({
  display: "flex",
  flex: 1,
  height: "100%",
  overflowY: "auto",
}));
const StyledCheckbox = styled("input")(({ theme }) => ({
  margin: theme.spacing(1),
  accentColor: theme.palette.primary.main,
}));
const StyledRow = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  marginBottom: theme.spacing(1),
  padding: theme.spacing(1, 2),
  borderRadius: theme.shape.borderRadius,
  transition: "background 0.2s ease",
  cursor: "pointer",
  "&:hover": {
    background: theme.palette.action.hover,
  },
}));

type SelectableNetworkMember = {
  userId: Guid;
  fullName: string;
  type: string;
};

type NetworkedGroupMarketplaceProps = {
  coworkers: UserCoworker[];
  networkConnections: UserInformation[];
  selectedMemberUserIds: Guid[];
  onMemberSelected?: (memberUserId: Guid) => void;
};

export default function NetworkedGroupSelector(props: Readonly<NetworkedGroupMarketplaceProps>) {
  const {
    coworkers,
    networkConnections,
    selectedMemberUserIds,
    onMemberSelected
  } = props;

  const selectableMembers = useMemo(() => {
    const members: SelectableNetworkMember[] = [
      ...coworkers.map(coworker => ({
        userId: coworker.userId,
        fullName: coworker.name.fullName,
        type: "coworker"
      })),
      ...networkConnections.map(connection => ({
        userId: connection.id,
        fullName: connection.name.fullName,
        type: "networkConnection"
      }))
    ];
    return _.uniqBy(members, (members => members.userId.value));
  }, [coworkers, networkConnections]);

  return (
    <MainContainer>
      <MainContent>
        <div>
          {selectableMembers.map(member => (
            <StyledRow
              key={member.userId.value}
              onClick={() => onMemberSelected?.(member.userId)}
            >
              <StyledCheckbox
                type="checkbox"
                checked={selectedMemberUserIds.some(i => i.isEqualTo(member.userId))}
                onChange={() => onMemberSelected?.(member.userId)}
                onClick={(e) => e.stopPropagation()}
              />
              <Tooltip title={member.type === "coworker" ? "Coworker" : "Network"}>
                <Typography>{member.fullName}</Typography>
              </Tooltip>
            </StyledRow>
          ))}
        </div>
      </MainContent>
    </MainContainer>
  );
}
